import { useMemo } from 'react'

import { useGetBlueprintFamilyGlossaryQuery } from '../../../api/core'
import { useLiveEventsTrackerAccessCheck } from '../../account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../account/hooks/useCurrentUserLanguage'
import { getTranslatedName } from '../../market-analytics/components/helpers/metricUtils'
import { TrackedGame } from '../types/TrackedGame'

type UseBlueprintFamilyForEventParams = {
  // Owning tracked game — carries the game-level blueprints and, on them, the
  // blueprint family each was categorized under.
  trackedGame?: TrackedGame
  // Game-level blueprint id of the event (`event.blueprintEvolutionData.blueprintId`).
  gameLevelBlueprintId?: string
}

export type BlueprintFamilyForEvent = {
  familyId?: string
  familyName?: string
  // The event's game-level blueprint exists but an analyst hasn't mapped it to a
  // family yet. Distinct from "no blueprint in hand": only this case justifies
  // telling the user the event isn't categorized.
  isUncategorized: boolean
  // There is a family to link to and the user may open its analytics page.
  canLink: boolean
  // The glossary carrying the family name is still being fetched. Never true
  // without the entitlement, since the request is skipped then.
  isFamilyNameLoading: boolean
}

/**
 * Resolves the blueprint family (market-level blueprint) a tracked live event
 * belongs to: event → game-level blueprint → `blueprintFamilyId` → family name.
 *
 * Shared by every LET surface that names the family (event dialog hierarchy
 * path, compact trail, calendar hover popup, feed cards) so they degrade
 * identically:
 * - **No blueprint in hand** (no tracked game loaded, or an event without a
 *   blueprint) → everything undefined, `isUncategorized: false`. Callers must
 *   stay silent; claiming "not categorized" here would be a guess.
 * - **Uncategorized blueprint** → `isUncategorized: true` with no `familyId`.
 * - **No analytics entitlement** (`RoleEnum.live_events_tracker`) → `canLink: false` and
 *   no name, since every family page is behind that role and the glossary
 *   request is skipped.
 * - **Glossary still in flight** → `familyId` set, `familyName` undefined,
 *   `isFamilyNameLoading: true`. Callers with a local fallback (e.g. the
 *   game-level blueprint name) can show that instead of flashing empty, but
 *   only while loading: the game-level name is not the family's name.
 */
export const useBlueprintFamilyForEvent = ({ trackedGame, gameLevelBlueprintId }: UseBlueprintFamilyForEventParams): BlueprintFamilyForEvent => {
  const userLanguage = useCurrentUserLanguage()
  const hasLiveEventsTrackerAccess = useLiveEventsTrackerAccessCheck()

  const gameLevelBlueprint = useMemo(
    () => (gameLevelBlueprintId ? trackedGame?.blueprints?.find((blueprint) => blueprint.blueprintId === gameLevelBlueprintId) : undefined),
    [trackedGame?.blueprints, gameLevelBlueprintId]
  )
  const familyId = gameLevelBlueprint?.blueprintFamilyId

  // Only the family's canonical name is missing locally, and only when there is
  // a family to name and a page to send the user to. The glossary list is
  // unscoped, so this shares its cache entry with the analytics catalogue and
  // the Data Glossary tab.
  const { data: glossary, isLoading: isGlossaryLoading } = useGetBlueprintFamilyGlossaryQuery(undefined, { skip: !familyId || !hasLiveEventsTrackerAccess })

  const familyName = useMemo(() => {
    if (!familyId) return undefined
    const item = (glossary ?? []).find((entry) => entry.id === familyId)
    return item ? getTranslatedName(item.names, userLanguage) : undefined
  }, [glossary, familyId, userLanguage])

  return {
    familyId,
    familyName,
    isUncategorized: !!gameLevelBlueprint && !familyId,
    canLink: !!familyId && hasLiveEventsTrackerAccess,
    isFamilyNameLoading: !!familyId && hasLiveEventsTrackerAccess && isGlossaryLoading,
  }
}
